/**
 * Client-side Flesch-Kincaid grade, so the readability meter can move WHILE the
 * specialist types. A server round-trip per keystroke would be unusable.
 *
 * This is a faithful port of the backend's pure-Python fallback
 * (`api/services/readability_scorer.py`). ⚠️ The backend prefers the `textstat`
 * library when it is installed, whose syllable counting is dictionary-based and
 * slightly more accurate — so a live number here can differ from the final
 * server score by a few tenths. The meter is live guidance; the score stored on
 * the fix, and the improve-or-refuse check at apply time, always come from the
 * backend.
 */

/** Heuristic syllable count (vowel groups, minus a trailing silent "e"). */
export function countSyllables(word: string): number {
	const w = word.toLowerCase().replace(/[^a-z]/g, "");
	if (!w) return 0;
	const groups = w.match(/[aeiouy]+/g);
	let count = groups ? groups.length : 0;
	if (w.endsWith("e") && !w.endsWith("le") && !w.endsWith("ie") && count > 1) {
		count -= 1;
	}
	return Math.max(1, count);
}

export function wordCount(text: string): number {
	return ((text || "").trim().match(/[A-Za-z]+(?:'[A-Za-z]+)?/g) || []).length;
}

/** Flesch-Kincaid grade level. Returns null when there is nothing to score. */
export function fleschKincaidGrade(text: string): number | null {
	const trimmed = (text || "").trim();
	if (!trimmed) return null;
	const words = trimmed.match(/[A-Za-z]+(?:'[A-Za-z]+)?/g) || [];
	if (words.length === 0) return null;
	const sentences = trimmed.match(/[.!?]+/g);
	const nSentences = Math.max(1, sentences ? sentences.length : 0);
	const nSyllables = words.reduce((sum, w) => sum + countSyllables(w), 0);
	const grade =
		0.39 * (words.length / nSentences) + 11.8 * (nSyllables / words.length) - 15.59;
	return Math.round(grade * 10) / 10;
}

export type ReadabilityBand = "easy" | "ok" | "hard" | "unscorable";

export interface ReadabilityReading {
	grade: number | null;
	band: ReadabilityBand;
	label: string;
	/** 0-100, where higher = easier to read, so the bar GROWS as copy improves. */
	quality: number;
	/** False when the text is too short for the formula to mean anything. */
	reliable: boolean;
}

// Bands align with the rewriter's own default grade threshold (12): at or above
// it a sentence is flagged as hard, so the meter must read red at that point.
export const EASY_MAX_GRADE = 8;
export const HARD_MIN_GRADE = 12;
const WORST_GRADE = 18; // college-graduate and beyond; the bottom of the scale

/**
 * Below this, Flesch-Kincaid says nothing useful. Mirrors the backend's
 * DEFAULT_MIN_SENTENCE_WORDS, which is why the rewriter never FLAGS a short
 * sentence in the first place.
 *
 * The formula is dominated by syllables-per-word on a short sentence, so one
 * ordinary trade word pins it in the red: "Our technicians repair water
 * heaters" scores 12.3 with nothing left to simplify. Colouring that red tells
 * the specialist their good edit made things worse, which is a lie.
 */
export const MIN_SCORABLE_WORDS = 12;

export function readGrade(text: string): ReadabilityReading | null {
	const grade = fleschKincaidGrade(text);
	if (grade === null) return null;

	if (wordCount(text) < MIN_SCORABLE_WORDS) {
		return {
			grade,
			band: "unscorable",
			label: "Too short to score",
			quality: 0,
			reliable: false,
		};
	}

	const band: ReadabilityBand =
		grade >= HARD_MIN_GRADE ? "hard" : grade > EASY_MAX_GRADE ? "ok" : "easy";

	const label =
		band === "hard"
			? "Hard to read"
			: band === "ok"
				? "Fairly easy"
				: "Easy to read";

	const clamped = Math.min(Math.max(grade, 0), WORST_GRADE);
	const quality = Math.round(100 - (clamped / WORST_GRADE) * 100);

	return { grade, band, label, quality, reliable: true };
}

/**
 * How much better (or worse) an edit is than what it replaced.
 *
 * On short sentences the absolute grade is meaningless but the DIRECTION still
 * is: turning one 30-word sentence into two short ones is a real improvement
 * even when both score badly. This is the honest success signal.
 */
export function describeImprovement(
	original: string,
	edited: string,
): { delta: number; direction: "better" | "worse" | "same"; summary: string } | null {
	const before = fleschKincaidGrade(original);
	const after = fleschKincaidGrade(edited);
	if (before === null || after === null) return null;

	const delta = Math.round((before - after) * 10) / 10;
	if (Math.abs(delta) < 0.5) {
		return { delta, direction: "same", summary: "About the same as the original" };
	}
	if (delta > 0) {
		return {
			delta,
			direction: "better",
			summary: `${delta.toFixed(1)} grades easier than the original`,
		};
	}
	return {
		delta,
		direction: "worse",
		summary: `${Math.abs(delta).toFixed(1)} grades harder than the original`,
	};
}
